import { useEffect, useMemo, useState, cloneElement, isValidElement } from 'react';
import i18n, {
  getCurrentLanguage,
  registerLanguagePreflight,
  waitForTranslation,
  type SupportedLanguage,
} from './config';

// Уже предупреждённые пары «язык:namespace». Предупреждение печатается ОДИН раз:
// прежняя проверка стояла в теле рендера и повторялась на каждый кадр, засоряя консоль
// тем сильнее, чем чаще перерисовывался экран.
const warnedNamespaces = new Set<string>();

const logDebug = (message: string, data: Record<string, unknown>, hypothesisId?: string) => {
  // Debug logging removed to prevent ERR_INSUFFICIENT_RESOURCES
};

/**
 * Рекурсивное слияние словарей перевода: значения из `overlay` побеждают, но
 * общие ветки не затираются целиком — иначе `{form: {...5 ключей}}` поверх
 * `{form: {...4 ключа}}` унёс бы разницу.
 */
export function deepMerge(
  base: Record<string, unknown>,
  overlay: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    const current = out[key];
    const bothObjects =
      !!current && typeof current === 'object' && !Array.isArray(current) &&
      !!value && typeof value === 'object' && !Array.isArray(value);
    out[key] = bothObjects
      ? deepMerge(current as Record<string, unknown>, value as Record<string, unknown>)
      : value;
  }
  return out;
}

async function loadNamespace(lang: string, ns: string): Promise<void> {
  logDebug('loadNamespace START', { lang, ns, hasBundle: i18n.hasResourceBundle(lang, ns), hasTranslation: i18n.hasResourceBundle(lang, 'translation') }, 'B');
  if (i18n.hasResourceBundle(lang, ns)) {
    logDebug('loadNamespace SKIP (already loaded)', { lang, ns });
    return;
  }
  // Пробуем несколько схем путей для гибкости структуры
  const tryPaths = [
    () => import(`./namespaces/${lang}/${ns}.json`),
    () => import(`./namespaces/${ns}/${lang}.json`),
  ] as const;
  for (const loader of tryPaths) {
    try {
      const mod = await loader();
      let bundle = (mod as any).default as Record<string, unknown>;
      // Если файл содержит обёртку { [ns]: {...} } — разворачиваем её, но НЕ
      // выбрасываем то, что лежит рядом. Раньше здесь стояло присваивание
      // bundle = bundle[ns], и все соседние разделы файла терялись молча:
      // в ru/devices.json это 6 разделов (copyFromDevice, form, tags,
      // notifications, configuredInterfaces, interfaceStacking), в
      // ru/settings.json — 2. Ключи из них не резолвились, и оператор видел
      // сырые идентификаторы (`form.interface_stacking` на карточке
      // устройства). Соседи кладутся ПОВЕРХ обёртки: там, где разделы
      // дублируются (devices.form vs form), соседний — надмножество.
      if (bundle && typeof bundle === 'object' && ns in bundle && typeof (bundle as any)[ns] === 'object') {
        const wrapped = (bundle as any)[ns] as Record<string, unknown>;
        const siblings = Object.fromEntries(
          Object.entries(bundle).filter(([key]) => key !== ns),
        );
        bundle = deepMerge(wrapped, siblings);
      }
      // Если файл пустой — используем fallback из базового translation
      if (!bundle || Object.keys(bundle).length === 0) {
        const base = i18n.getResourceBundle(lang, 'translation') as Record<string, unknown> | undefined;
        logDebug('loadNamespace empty bundle, checking translation', { lang, ns, hasBase: !!base, baseKeys: base ? Object.keys(base).slice(0, 5) : [] }, 'B');
        const group = base && (base as any)[ns];
        if (group && typeof group === 'object') {
          // Добавляем БЕЗ обёртки, чтобы ключи были на корневом уровне namespace
          i18n.addResourceBundle(lang, ns, group, true, true);
          logDebug('loadNamespace added from translation fallback', { lang, ns, hasBundle: i18n.hasResourceBundle(lang, ns) });
          return;
        }
      } else {
        // ВАЖНО: i18next при поиске ключа в namespace ищет ключ БЕЗ префикса namespace
        // Например, t('devices:stats.interfaces') ищет ключ 'stats.interfaces' в namespace 'devices'
        // Поэтому добавляем bundle БЕЗ обёртки, чтобы ключи были на корневом уровне namespace
        i18n.addResourceBundle(lang, ns, bundle, true, true);
        logDebug('loadNamespace bundle added', { lang, ns, hasBundle: i18n.hasResourceBundle(lang, ns), bundleKeys: Object.keys(bundle).slice(0, 5) });
      }
      return;
    } catch {
      // продолжаем к следующему пути
    }
  }
  // Fallback: берём группу из базового 'translation' и добавляем как namespace
  try {
    const base = i18n.getResourceBundle(lang, 'translation') as Record<string, unknown> | undefined;
    logDebug('loadNamespace fallback to translation', { lang, ns, hasBase: !!base }, 'B');
    const group = base && (base as any)[ns];
    if (group && typeof group === 'object') {
      // Добавляем БЕЗ обёртки, чтобы ключи были на корневом уровне namespace
      i18n.addResourceBundle(lang, ns, group, true, true);
      logDebug('loadNamespace added from translation', { lang, ns, hasBundle: i18n.hasResourceBundle(lang, ns) });
    }
  } catch {
    // no-op
  }
}

export async function ensureNamespaces(
  namespaces: string[],
  lang: SupportedLanguage = getCurrentLanguage(),
): Promise<void> {
  logDebug('ensureNamespaces START', { lang, namespaces, hasTranslation: i18n.hasResourceBundle(lang, 'translation'), isInitialized: i18n.isInitialized }, 'B');
  // ✅ КРИТИЧНО: Ждем загрузки базового translation перед загрузкой других namespaces
  await waitForTranslation(lang);
  logDebug('ensureNamespaces translation loaded', { lang, hasTranslation: i18n.hasResourceBundle(lang, 'translation') }, 'B');
  await Promise.all(namespaces.map((ns) => loadNamespace(lang, ns)));
  logDebug('ensureNamespaces DONE', { lang, namespaces, allLoaded: namespaces.every(ns => i18n.hasResourceBundle(lang, ns)) });
}

async function preflightNamespaces(
  namespaces: string[],
  lang: SupportedLanguage,
): Promise<void> {
  await ensureNamespaces(namespaces, lang);
  const missing = namespaces.filter((namespace) => !i18n.hasResourceBundle(lang, namespace));
  if (missing.length > 0) {
    throw new Error(`Missing i18n namespaces for ${lang}: ${missing.join(', ')}`);
  }
}

interface I18nNamespacesProps {
  ns: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function I18nNamespaces({ ns, children, fallback = null }: I18nNamespacesProps) {
  const [lang, setLang] = useState<SupportedLanguage>(getCurrentLanguage);
  const [attemptedLanguage, setAttemptedLanguage] = useState<SupportedLanguage | null>(null);

  // ✅ ФИКС (2026-07-26): стабильная ссылка на список namespace.
  //
  // `ns` приходит литералом прямо из JSX (`ns={['common', 'auth', ...]}`), то есть это НОВЫЙ
  // массив на каждый рендер родителя. Он стоял в зависимостях useEffect, поэтому эффект
  // перезапускался на каждом рендере: cleanup предыдущего ставил isMounted = false, и
  // `setReady(true)` из уже улетевшего промиса отбрасывался. При достаточно частых рендерах
  // ready не становился true НИКОГДА — экран навечно оставался на fallback-спиннере, хотя
  // словари были загружены (замер на /monitoring/snmp: allNamespacesLoaded=true, ready=false).
  // Снаружи это неотличимо от «страница грузится»: оператор ждёт и уходит.
  const nsKey = ns.join(',');
  const nsList = useMemo(() => nsKey.split(',').filter(Boolean), [nsKey]);

  // ✅ ФИКС (2026-09-20): ready сидируется СИНХРОННО, если namespace уже в памяти.
  //
  // `I18nNamespaces` стоит в element каждого <Route> (router.tsx), поэтому переход между
  // страницами создаёт НОВЫЙ инстанс компонента. `ready` раньше всегда стартовал false и
  // флипался в true только после async ensureNamespaces() — даже если бандл уже загружен
  // предыдущей страницей. Результат: fallback-спиннер (PageFallback) мелькал на КАЖДОЙ
  // навигации, а не только на первой холодной загрузке namespace — читалось как «глобальный
  // экран загрузки на каждом экране».
  const [ready, setReady] = useState(() => nsList.every((n) => i18n.hasResourceBundle(lang, n)));

  useEffect(() => {
    return registerLanguagePreflight((targetLanguage) => (
      preflightNamespaces(nsList, targetLanguage)
    ));
  }, [nsList]);

  useEffect(() => {
    const handleLanguageChanged = () => setLang(getCurrentLanguage());
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useEffect(() => {
    logDebug('I18nNamespaces MOUNT', { ns, lang, hasTranslation: i18n.hasResourceBundle(lang, 'translation'), isInitialized: i18n.isInitialized }, 'A');
  }, []);

  useEffect(() => {
    let isMounted = true;
    setAttemptedLanguage(null);
    logDebug('I18nNamespaces useEffect START', { ns, lang, ready, hasTranslation: i18n.hasResourceBundle(lang, 'translation') }, 'A');
    ensureNamespaces(nsList, lang).then(() => {
      logDebug('I18nNamespaces ensureNamespaces DONE', { ns, lang, isMounted }, 'C');
      // Единственный момент, когда отсутствие бандла — ФАКТ, а не переходное состояние:
      // все попытки загрузки завершились. Раньше проверка стояла в теле рендера и
      // срабатывала в окне между `setReady(true)` и приходом состояния
      // `allNamespacesLoaded`, то есть ругалась на словари, которые через мгновение
      // отрисовывались правильно. Живой замер 2026-09-02: на экране настроек биллинга
      // предупреждение печаталось про `billing, settings`, а оба текста выводились
      // по-русски корректно.
      //
      // Ложная тревога хуже молчания: она приучает не смотреть в консоль, и НАСТОЯЩИЙ
      // ненайденный словарь (прецедент /monitoring/snmp, ns `snmpMonitoring`) утонет
      // среди шума. Каталог операторской читаемости называет это отдельным классом —
      // «красный сигнал, который остаётся красным без дефекта».
      const missing = nsList.filter((n) => !i18n.hasResourceBundle(lang, n));
      for (const name of missing) {
        const key = `${lang}:${name}`;
        if (warnedNamespaces.has(key)) continue;
        warnedNamespaces.add(key);
        console.warn(
          `[i18n] namespace не загрузился: ${name} (lang=${lang}). ` +
            'Экран отрисован, но ключи этого namespace покажутся как идентификаторы.',
        );
      }
      if (isMounted) {
        setAttemptedLanguage(lang);
        // ✅ КРИТИЧНО: Устанавливаем ready сразу после загрузки namespaces
        // Проверка готовности будет в условии рендера через allNamespacesLoaded и isInitialized
        setReady(true);
        logDebug('I18nNamespaces setReady(true)', { ns, lang }, 'C');
      }
    });
    return () => {
      isMounted = false;
    };
  }, [lang, nsList]);

  // ✅ КРИТИЧНО: Подписываемся на события загрузки переводов для ре-рендера
  useEffect(() => {
    const handleLoaded = () => {
      logDebug('I18nNamespaces i18n loaded event', { ns, lang, hasTranslation: i18n.hasResourceBundle(lang, 'translation') }, 'C');
      // Проверяем, загружены ли все необходимые namespaces
      const allLoaded = nsList.every((n) => i18n.hasResourceBundle(lang, n));
      if (allLoaded) {
        if (!ready) {
          setReady(true);
        }
        logDebug('I18nNamespaces setReady(true) from event', { ns, lang }, 'C');
      }
    };

    i18n.on('loaded', handleLoaded);
    i18n.on('languageChanged', handleLoaded);

    return () => {
      i18n.off('loaded', handleLoaded);
      i18n.off('languageChanged', handleLoaded);
    };
  }, [lang, nsList, ready]);

  // ✅ КРИТИЧНО: Проверяем, что все namespaces действительно загружены перед рендером
  // Используем useState для реактивности при изменении состояния i18n
  const [allNamespacesLoaded, setAllNamespacesLoaded] = useState(() => {
    return nsList.every((n) => i18n.hasResourceBundle(lang, n));
  });

  // Обновляем allNamespacesLoaded при изменении языка или namespaces
  useEffect(() => {
    const checkLoaded = () => {
      const loaded = nsList.every((n) => i18n.hasResourceBundle(lang, n));
      setAllNamespacesLoaded(loaded);
    };
    checkLoaded();
    // Подписываемся на события для обновления состояния
    i18n.on('loaded', checkLoaded);
    i18n.on('languageChanged', checkLoaded);
    return () => {
      i18n.off('loaded', checkLoaded);
      i18n.off('languageChanged', checkLoaded);
    };
  }, [lang, nsList]);


  // ✅ КРИТИЧНО: Проверяем все условия готовности перед рендером
  // ready - установлен после загрузки namespaces
  // allNamespacesLoaded - все namespaces загружены в i18n
  // Также проверяем, что i18n инициализирован
  // ✅ ФИКС (2026-07-26): экран НЕ блокируется навсегда из-за одного не найденного namespace.
  //
  // Раньше условие требовало allNamespacesLoaded — «все ns есть в i18n». Если хоть один ns
  // не резолвился (нет файла, опечатка в имени, нет группы в translation), hasResourceBundle
  // навсегда оставался false, и страница вечно показывала fallback-спиннер. Именно так
  // /monitoring/snmp был недоступен целиком: в списке ns роута стоит `snmpMonitoring`.
  // Снаружи это неотличимо от «страница грузится» — оператор ждёт и уходит.
  //
  // Теперь ждём только ЗАВЕРШЕНИЯ попытки загрузки (ready). Недостающие ключи отрисуются
  // как идентификаторы — это видно, чинится и не мешает пользоваться экраном.
  const isFullyReady = ready && i18n.isInitialized;



  if (!isFullyReady) {
    logDebug('I18nNamespaces NOT READY (showing fallback)', { 
      ns, 
      lang, 
      ready, 
      allNamespacesLoaded,
      isInitialized: i18n.isInitialized,
      isFullyReady,
      hasTranslation: i18n.hasResourceBundle(lang, 'translation'),
      namespacesStatus: ns.map(n => ({ ns: n, loaded: i18n.hasResourceBundle(lang, n) }))
    }, 'D');
    return fallback ?? null;
  }
  logDebug('I18nNamespaces READY (rendering children)', { 
    ns, 
    lang, 
    hasTranslation: i18n.hasResourceBundle(lang, 'translation'), 
    allNamespacesLoaded,
    namespacesStatus: ns.map(n => ({ ns: n, loaded: i18n.hasResourceBundle(lang, n) }))
  }, 'C');
  // ✅ ФИКС (2026-07-26): дети рендерятся напрямую.
  //
  // Раньше здесь стояла обёртка I18nWrapper, которую useMemo пересоздавал при каждом
  // изменении renderKey. Для React это НОВЫЙ ТИП компонента, поэтому всё поддерево (включая
  // lazy-страницу) размонтировалось и монтировалось заново. Вместе с loadNamespace, который
  // дёргал i18n.changeLanguage() ради «принудительного ре-рендера», получался замкнутый цикл:
  // changeLanguage → событие languageChanged → setRenderKey → новый тип обёртки →
  // перемонтирование → снова загрузка namespace → changeLanguage. Экран /monitoring/snmp
  // из-за этого не открывался вовсе: ready не успевал стать true между перемонтированиями.
  //
  // Форсировать ре-рендер не нужно: дети монтируются уже ПОСЛЕ ensureNamespaces, то есть
  // с готовыми словарями, а изменение ready и так вызывает рендер.
  return <>{children}</>;
}
